export type ImageCrop = { x: number; y: number; width: number; height: number };
export type ImageZoomMode = 'zoomToPoi' | 'zoomToFit';

export type ImagePlacement = {
    scale: number;
    panX: number;
    panY: number;
    renderedWidth: number;
    renderedHeight: number;
    crop: ImageCrop;
    selectedTarget: string;
};

const targetFactors = [1 / 3, 1 / 2, 2 / 3] as const;
const targetBoundaries = [5 / 12, 7 / 12] as const;
const targetNames = [
    ['top-left', 'top-center', 'top-right'],
    ['middle-left', 'center', 'middle-right'],
    ['bottom-left', 'bottom-center', 'bottom-right']
] as const;

function clamp(value: number, minimum: number, maximum: number): number {
    return Math.min(Math.max(value, minimum), maximum);
}

function requirePositiveFinite(name: string, value: number): void {
    if (!Number.isFinite(value) || value <= 0) throw new RangeError(`${name} must be a positive finite number.`);
}

function selectTargetIndex(normalizedPoi: number): 0 | 1 | 2 {
    if (normalizedPoi < targetBoundaries[0]) return 0;
    if (normalizedPoi < targetBoundaries[1]) return 1;
    return 2;
}

export function calculateImagePlacement(options: {
    imageWidth: number;
    imageHeight: number;
    frameWidth: number;
    frameHeight: number;
    poiX: number;
    poiY: number;
    zoomMode?: ImageZoomMode;
}): ImagePlacement {
    const { imageWidth, imageHeight, frameWidth, frameHeight } = options;
    requirePositiveFinite('imageWidth', imageWidth);
    requirePositiveFinite('imageHeight', imageHeight);
    requirePositiveFinite('frameWidth', frameWidth);
    requirePositiveFinite('frameHeight', frameHeight);
    if (!Number.isFinite(options.poiX) || !Number.isFinite(options.poiY)) throw new TypeError('POI coordinates must be finite.');

    if (options.zoomMode === 'zoomToFit') {
        const scale = Math.min(frameWidth / imageWidth, frameHeight / imageHeight);
        const renderedWidth = imageWidth * scale;
        const renderedHeight = imageHeight * scale;
        return {
            scale,
            panX: (frameWidth - renderedWidth) / 2,
            panY: (frameHeight - renderedHeight) / 2,
            renderedWidth,
            renderedHeight,
            crop: { x: 0, y: 0, width: imageWidth, height: imageHeight },
            selectedTarget: 'center'
        };
    }

    const scale = Math.max(frameWidth / imageWidth, frameHeight / imageHeight);
    const cropWidth = Math.min(imageWidth, frameWidth / scale);
    const cropHeight = Math.min(imageHeight, frameHeight / scale);
    const normalizedPoiX = clamp(options.poiX, 0, 1);
    const normalizedPoiY = clamp(options.poiY, 0, 1);
    const xIndex = selectTargetIndex(normalizedPoiX);
    const yIndex = selectTargetIndex(normalizedPoiY);
    const cropX = clamp(normalizedPoiX * imageWidth - cropWidth * targetFactors[xIndex], 0, Math.max(0, imageWidth - cropWidth));
    const cropY = clamp(normalizedPoiY * imageHeight - cropHeight * targetFactors[yIndex], 0, Math.max(0, imageHeight - cropHeight));

    return {
        scale,
        panX: -cropX * scale,
        panY: -cropY * scale,
        renderedWidth: imageWidth * scale,
        renderedHeight: imageHeight * scale,
        crop: { x: cropX, y: cropY, width: cropWidth, height: cropHeight },
        selectedTarget: targetNames[yIndex][xIndex]
    };
}
